import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, PenLine, Trash2, Eye, EyeOff, Save, Plus, ArrowLeft, ExternalLink } from "lucide-react";
import FileUpload from "@/components/FileUpload";

const EMPTY = { title: "", excerpt: "", body_md: "", cover_image_url: "", published: false };

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // post being edited, or EMPTY to create
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/blog/posts");
      setPosts(data.results);
    } catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = (p) => setEditing({ ...p });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.title.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      if (editing.id) {
        const { data } = await api.put(`/admin/blog/posts/${editing.id}`, {
          title: editing.title, excerpt: editing.excerpt,
          body_md: editing.body_md, cover_image_url: editing.cover_image_url,
          published: editing.published,
        });
        toast.success(data.published ? "Post updated & published" : "Post saved as draft");
      } else {
        const { data } = await api.post("/admin/blog/posts", {
          title: editing.title, excerpt: editing.excerpt,
          body_md: editing.body_md, cover_image_url: editing.cover_image_url,
          published: editing.published,
        });
        toast.success(data.published ? "Post published" : "Draft saved");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/posts/${p.id}`);
      toast.success("Post deleted");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const onCoverUpload = (upload) => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    setEditing((e) => ({ ...e, cover_image_url: `${backend}/api/uploads/${upload.path}` }));
    toast.success("Cover image attached");
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="admin-blog">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold">
              <ArrowLeft className="w-4 h-4" /> Back to admin
            </Link>
            <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900 flex items-center gap-2">
              <PenLine className="w-6 h-6 text-sage-600" /> Blog editor
            </h1>
          </div>
          {!editing && (
            <button type="button" onClick={startNew}
              data-testid="admin-blog-new"
              className="btn-primary">
              <Plus className="w-4 h-4" /> New post
            </button>
          )}
        </div>

        {editing ? (
          <div className="mt-8 paper-card p-6 space-y-5" data-testid="admin-blog-editor">
            <div>
              <label className="field-label">Title</label>
              <input value={editing.title} data-testid="admin-blog-title"
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-lg font-heading font-semibold focus:ring-2 focus:ring-sage-500" />
            </div>
            <div>
              <label className="field-label">Excerpt <span className="text-stone2-400 font-normal">(short summary shown on the blog list)</span></label>
              <textarea value={editing.excerpt} data-testid="admin-blog-excerpt"
                onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                rows={2} maxLength={280}
                className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500" />
            </div>
            <div>
              <label className="field-label">Cover image <span className="text-stone2-400 font-normal">(optional)</span></label>
              <div className="flex items-center gap-3 flex-wrap">
                <input type="url" placeholder="https://..." value={editing.cover_image_url || ""}
                  data-testid="admin-blog-cover-url"
                  onChange={(e) => setEditing({ ...editing, cover_image_url: e.target.value })}
                  className="flex-1 min-w-[240px] rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                <FileUpload testId="admin-blog-cover-upload" label="Or upload" onUploaded={onCoverUpload} />
              </div>
              {editing.cover_image_url && (
                <img src={editing.cover_image_url} alt="" className="mt-2 rounded-lg max-h-32 object-cover" />
              )}
            </div>
            <div>
              <label className="field-label">
                Body — Markdown supported (**bold**, ## headings, [links](url), * lists, etc.)
              </label>
              <textarea value={editing.body_md} data-testid="admin-blog-body"
                onChange={(e) => setEditing({ ...editing, body_md: e.target.value })}
                rows={16}
                placeholder="## Welcome to our blog&#10;&#10;Write your first post in **markdown**..."
                className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-3 text-sm font-mono focus:ring-2 focus:ring-sage-500" />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-sage-900 cursor-pointer">
              <input type="checkbox" checked={!!editing.published}
                data-testid="admin-blog-publish-toggle"
                onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                className="rounded text-sage-600" />
              Publish immediately <span className="text-stone2-500 text-xs">(uncheck to save as draft)</span>
            </label>
            <div className="flex items-center gap-2 pt-2">
              <button type="button" onClick={save} disabled={saving}
                data-testid="admin-blog-save"
                className="btn-primary">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editing.id ? "Save changes" : "Create post"}
              </button>
              <button type="button" onClick={cancel} className="btn-outline-sage">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="mt-8 paper-card overflow-hidden" data-testid="admin-blog-list">
            {posts.length === 0 ? (
              <div className="p-10 text-center text-stone2-500">
                No posts yet. Click "New post" to write your first.
              </div>
            ) : (
              <ul className="divide-y divide-stone2-100">
                {posts.map((p) => (
                  <li key={p.id} className="p-4 flex items-start justify-between gap-3" data-testid={`admin-blog-row-${p.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading font-semibold text-sage-900 truncate">{p.title}</span>
                        {p.published ? (
                          <span className="chip"><Eye className="w-3 h-3 mr-1" /> Published</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-stone2-100 text-stone2-700">
                            <EyeOff className="w-3 h-3 inline mr-1" /> Draft
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-stone2-500">
                        {p.published_at ? `Published ${new Date(p.published_at).toLocaleDateString()}` : "Not yet published"}
                        · {p.view_count || 0} views
                        · /blog/{p.slug}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {p.published && (
                        <Link to={`/blog/${p.slug}`} target="_blank"
                          className="text-xs text-sage-700 hover:text-sage-900 font-semibold inline-flex items-center gap-1">
                          View <ExternalLink className="w-3 h-3" />
                        </Link>
                      )}
                      <button type="button" onClick={() => startEdit(p)}
                        data-testid={`admin-blog-edit-${p.id}`}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-sage-600 text-white hover:bg-sage-700">
                        Edit
                      </button>
                      <button type="button" onClick={() => remove(p)}
                        data-testid={`admin-blog-delete-${p.id}`}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
